/* eslint-disable max-lines */
import React, {
  useEffect, useState
} from 'react';
import {
  opcodeAssistantService
} from 'shared/api';
import {
 useParams
} from 'react-router-dom';
import {
  PageContent,
  PageContentHeader,
  PageContentHeaderText,
  PageContentHeaderTitle,
  PageDescription,
  PageHeader,
  PageHeaderAction,
  PageLayout,
  PageSubTitle,
  PageTitle,
  PageTitles,
} from 'shared/components/NewDesignComponents/PageLayout';
import {
 Button
} from 'shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'shared/components/ui/dialog';
import ManageAssistantDialog from '../components/UpdateAssistant';
import {
  SearchBar
} from 'builders/builderInternal/components/Instructions/Filters';
import AssistantCard from '../components/AssistantCard';
import CreateAssistantWizard from '../components/CreateAssistantWizard';
import {
 Assistant
} from '../types/builderAssistant.types';
import { PaginationBar } from 'shared/components/NewDesignComponents/PaginationBar';

const ITEMS_PER_PAGE = 12;

// `channels` on the Assistant doc is the policy object
// `{mode, authorized_channels}` — not an array of channel records.
// Pass it through unchanged; only dates still need normalizing.
function normalizeAssistantData(apiAssistant: any): Assistant {
  const normalizeDate = (date: any) =>
    date instanceof Date ? date.toISOString() : String(date);

  return {
    ...apiAssistant,
    created: normalizeDate(apiAssistant.created),
    updated: apiAssistant.updated ? normalizeDate(apiAssistant.updated) : undefined,
  };
}

export default function Assistants() {
  const [assistantInstances, setAssistantInstances] = useState<Assistant[]>([]);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);
  const [updateModal, setUpdateModal] = useState(false);
  const [selectedCard, setSelectedCard] = useState<Assistant | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [serverTotalPages, setServerTotalPages] = useState(1);

  const {
 oragID, subOragID
} = useParams<{ oragID: string; subOragID: string }>();

  const getFilteredList = () => {
    return assistantInstances.filter(
      (instance) =>
        instance.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        instance.assistant_id?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const fetchAssistants = (page: number) => {
    setLoading(true);
    opcodeAssistantService
      .listOpcodeAssistants(oragID, subOragID, page, ITEMS_PER_PAGE)
      .then((res) => {
        const payload = res.data;
        if (payload && typeof payload === 'object' && 'data' in payload && 'pagination' in payload) {
          const p = payload as { data: any[]; pagination: { totalPages: number } };
          setAssistantInstances(p.data.map((a: any) => normalizeAssistantData(a)));
          setServerTotalPages(p.pagination.totalPages);
        } else {
          setAssistantInstances(
            (Array.isArray(payload) ? payload : []).map((a: any) => normalizeAssistantData(a))
          );
        }
      })
      .catch((err) => {
        console.error('Failed to load assistants:', err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchAssistants(1);
  }, [oragID, subOragID]);

  useEffect(() => {
    if (currentPage > 1) fetchAssistants(currentPage);
  }, [currentPage]);

  return (
      <PageLayout>
          <PageHeader>
              <PageTitles>
                  <PageTitle>Build your Enterprise</PageTitle>
                  <PageSubTitle>Agentic Workforce</PageSubTitle>
                  <PageDescription>
                      Empower your business with AI-driven assistants that work alongside your team. Automate tasks,
                      enhance decision-making, and drive productivity with a workforce built for the future.
                  </PageDescription>
              </PageTitles>
              <PageHeaderAction>
                  <Button onClick={() => setShow(true)} className="w-full">
                      Add new assistant
                  </Button>
              </PageHeaderAction>
          </PageHeader>

          <PageContent loading={loading}>
              <PageContentHeader>
                  <PageContentHeaderTitle>Assistant Builder:</PageContentHeaderTitle>
                  <PageContentHeaderText>Manage your Agentic AI Assistants</PageContentHeaderText>
              </PageContentHeader>

              <div className="w-full mx-auto p-2 md:p-6">
                  <div className="space-y-4">
                      <SearchBar onSearch={handleSearch} />
                      {(() => {
                        const filteredList = getFilteredList();
                        return filteredList.length > 0 ? (
                          <>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                              {filteredList.map((item) => (
                                <AssistantCard
                                  key={item.assistant_id}
                                  onManage={() => {
                                    setSelectedCard(item);
                                    setUpdateModal(true);
                                  }}
                                  {...item}
                                  setAssistantInstances={setAssistantInstances}
                                />
                              ))}
                            </div>
                            <PaginationBar currentPage={currentPage} totalPages={serverTotalPages} onPageChange={setCurrentPage} />
                          </>
                        ) : (
                          <div className="flex flex-grow justify-center items-center">
                            <span className="text-lg font-semibold mb-2">No Activated Assistant Found</span>
                          </div>
                        );
                      })()}
                  </div>
              </div>

              {/* create new assistant dialog */}
              <CreateNewAssistantDialog show={show} setShow={setShow} setAssistantInstances={setAssistantInstances} />

              {/* update assistant dialog */}
              {selectedCard && (
              <ManageAssistantDialog
            show={updateModal}
            setShow={setUpdateModal}
            selectedCard={selectedCard}
            setAssistantInstances={setAssistantInstances}
          />
        )}
          </PageContent>
      </PageLayout>
  );
}

interface CreateNewAssistantDialogProps {
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  setAssistantInstances: React.Dispatch<React.SetStateAction<Assistant[]>>;
}

const CreateNewAssistantDialog = ({
  show,
  setShow,
  setAssistantInstances,
}: CreateNewAssistantDialogProps) => {

  return (
      <Dialog open={show} onOpenChange={setShow}>
          <DialogContent className="w-full min-h-[550px] max-w-2xl p-4 rounded-lg shadow-lg">
              <DialogHeader>
                  <DialogTitle className="text-2xl font-semibold">Create new assistant</DialogTitle>
                  <DialogDescription className="text-md text-muted-foreground">
                      Set up a new assistant and link it to an opcode.
                  </DialogDescription>
              </DialogHeader>

              <CreateAssistantWizard setShow={setShow} setAssistantInstances={setAssistantInstances} />
          </DialogContent>
      </Dialog>
  );
};
// rebuild Sun Apr 19 04:06:30 UTC 2026
